// src/services/adminService.js
// Admin-only Firebase operations for client management, file uploads, etc.
// Separated from firebase.js to keep client-facing code clean.

import { db, storage, devClientConfig } from "./firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  query,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  deleteObject,
  listAll,
  getDownloadURL,
  getMetadata,
} from "firebase/storage";

const DEV_MODE = import.meta.env.VITE_DEV_MODE === "true";

// ============================================
// UTILITY: STRIP UNDEFINED VALUES
// ============================================

/**
 * Recursively remove all keys with `undefined` values from an object.
 * Firestore rejects documents containing `undefined` anywhere in the tree.
 * This is a safety net; source code should avoid producing `undefined` in
 * the first place, but this ensures nothing slips through.
 */
const stripUndefined = (obj) => {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj)) return obj.map(stripUndefined);
  // Skip non-plain objects (e.g., Firestore FieldValue sentinels, Dates)
  if (obj.constructor && obj.constructor !== Object) return obj;

  return Object.fromEntries(
    Object.entries(obj)
      .filter(([, v]) => v !== undefined)
      .map(([k, v]) => [k, stripUndefined(v)]),
  );
};

// ============================================
// DEV MODE MOCK DATA
// ============================================

const mockClients = [
  {
    id: "laundry-lifesaver",
    status: "active",
    createdAt: new Date("2026-02-01"),
    updatedAt: new Date("2026-02-15"),
    config: devClientConfig,
  },
];

// ============================================
// CLIENT CRUD
// ============================================

/**
 * List all clients from Firestore.
 * Returns summary data for the client list view.
 */
export const listClients = async () => {
  if (DEV_MODE) {
    await new Promise((r) => setTimeout(r, 300));
    return mockClients;
  }

  try {
    const snapshot = await getDocs(collection(db, "clients"));
    return snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));
  } catch (error) {
    console.error("Error listing clients:", error);
    throw error;
  }
};

/**
 * Get a single client document from Firestore.
 */
export const getClient = async (slug) => {
  if (DEV_MODE) {
    await new Promise((r) => setTimeout(r, 200));
    const client = mockClients.find((c) => c.id === slug);
    return client || null;
  }

  try {
    const docSnap = await getDoc(doc(db, "clients", slug));
    if (!docSnap.exists()) return null;
    return { id: docSnap.id, ...docSnap.data() };
  } catch (error) {
    console.error("Error getting client:", error);
    throw error;
  }
};

/**
 * Create a new client in Firestore.
 * @param {string} slug - URL-safe client slug (used as document ID)
 * @param {object} config - Full v2 client config object
 */
export const createClient = async (slug, config) => {
  if (DEV_MODE) {
    console.log("🔧 DEV MODE: Mock create client", slug);
    await new Promise((r) => setTimeout(r, 500));
    return { id: slug, status: "draft", config };
  }

  try {
    // Check if slug already exists
    const existing = await getDoc(doc(db, "clients", slug));
    if (existing.exists()) {
      throw new Error(`Client "${slug}" already exists.`);
    }

    const clientData = {
      status: "draft",
      config: { ...config, clientSlug: slug },
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(doc(db, "clients", slug), stripUndefined(clientData));
    return { id: slug, ...clientData };
  } catch (error) {
    console.error("Error creating client:", error);
    throw error;
  }
};

/**
 * Update an existing client's config in Firestore.
 * @param {string} slug - Client slug (document ID)
 * @param {object} updates - Partial update object (can include config, status, etc.)
 */
export const updateClient = async (slug, updates) => {
  if (DEV_MODE) {
    console.log("🔧 DEV MODE: Mock update client", slug, updates);
    await new Promise((r) => setTimeout(r, 400));
    return { id: slug, ...updates };
  }

  try {
    const docRef = doc(db, "clients", slug);
    await updateDoc(
      docRef,
      stripUndefined({
        ...updates,
        updatedAt: serverTimestamp(),
      }),
    );
    return { id: slug, ...updates };
  } catch (error) {
    console.error("Error updating client:", error);
    throw error;
  }
};

/**
 * Update just the client's status field.
 */
export const updateClientStatus = async (slug, status) => {
  return updateClient(slug, { status });
};

/**
 * Delete a client from Firestore and optionally clean up Storage.
 * @param {string} slug - Client slug
 * @param {boolean} deleteFiles - Whether to also delete Storage files
 */
export const deleteClient = async (slug, deleteFiles = false) => {
  if (DEV_MODE) {
    console.log("🔧 DEV MODE: Mock delete client", slug);
    await new Promise((r) => setTimeout(r, 500));
    return true;
  }

  try {
    // Delete Firestore document
    await deleteDoc(doc(db, "clients", slug));

    // Optionally delete all files in Storage
    if (deleteFiles) {
      try {
        await deleteStorageDirectory(`clients/${slug}`);
      } catch (storageError) {
        console.warn("Could not delete storage files:", storageError);
        // Don't fail the whole operation if storage cleanup fails
      }
    }

    return true;
  } catch (error) {
    console.error("Error deleting client:", error);
    throw error;
  }
};

// ============================================
// FILE UPLOAD / MANAGEMENT
// ============================================

/**
 * Upload a file to Firebase Storage.
 * @param {string} storagePath - Full path in Storage (e.g., "clients/abc/scored/data.json")
 * @param {File} file - File object from input
 * @param {object} metadata - Optional metadata
 * @returns {Promise<{path: string, url: string}>}
 */
export const uploadFile = async (storagePath, file, metadata = {}) => {
  if (DEV_MODE) {
    console.log("🔧 DEV MODE: Mock upload", storagePath, file.name);
    await new Promise((r) => setTimeout(r, 800));
    return { path: storagePath, url: `#dev-mock-url/${storagePath}` };
  }

  try {
    const storageRef = ref(storage, storagePath);
    const customMetadata = {
      contentType: file.type || "application/octet-stream",
      customMetadata: {
        originalName: file.name,
        uploadedAt: new Date().toISOString(),
        ...metadata,
      },
    };

    await uploadBytes(storageRef, file, customMetadata);
    const url = await getDownloadURL(storageRef);
    return { path: storagePath, url };
  } catch (error) {
    console.error("Error uploading file:", error);
    throw error;
  }
};

/**
 * Delete a file from Firebase Storage.
 */
export const deleteFile = async (storagePath) => {
  if (DEV_MODE) {
    console.log("🔧 DEV MODE: Mock delete file", storagePath);
    return true;
  }

  try {
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
    return true;
  } catch (error) {
    if (error.code === "storage/object-not-found") {
      console.warn("File not found, already deleted:", storagePath);
      return true;
    }
    console.error("Error deleting file:", error);
    throw error;
  }
};

/**
 * List all files in a Storage directory.
 * Returns array of { name, fullPath, size, updatedAt }.
 */
export const listFiles = async (directoryPath) => {
  if (DEV_MODE) {
    console.log("🔧 DEV MODE: Mock list files", directoryPath);
    return [];
  }

  try {
    const dirRef = ref(storage, directoryPath);
    const result = await listAll(dirRef);

    const files = await Promise.all(
      result.items.map(async (itemRef) => {
        try {
          const metadata = await getMetadata(itemRef);
          return {
            name: itemRef.name,
            fullPath: itemRef.fullPath,
            size: metadata.size,
            contentType: metadata.contentType,
            updatedAt: metadata.updated,
          };
        } catch {
          return {
            name: itemRef.name,
            fullPath: itemRef.fullPath,
          };
        }
      }),
    );

    return files;
  } catch (error) {
    console.error("Error listing files:", error);
    throw error;
  }
};

/**
 * List file paths in a Storage directory (lightweight — no metadata calls).
 * Uses a single listAll() call. Returns array of { name, fullPath }.
 * Use this for existence checks where file size/date aren't needed.
 */
export const listFilePaths = async (directoryPath) => {
  if (DEV_MODE) {
    console.log("🔧 DEV MODE: Mock list file paths", directoryPath);
    return [];
  }

  try {
    const dirRef = ref(storage, directoryPath);
    const result = await listAll(dirRef);
    return result.items.map((itemRef) => ({
      name: itemRef.name,
      fullPath: itemRef.fullPath,
    }));
  } catch (error) {
    console.error("Error listing file paths:", error);
    throw error;
  }
};

/**
 * Check if a file exists in Firebase Storage.
 */
export const fileExists = async (storagePath) => {
  if (DEV_MODE) return false;

  try {
    const storageRef = ref(storage, storagePath);
    await getMetadata(storageRef);
    return true;
  } catch (error) {
    if (error.code === "storage/object-not-found") return false;
    throw error;
  }
};

/**
 * Delete all files in a Storage directory (recursive).
 */
const deleteStorageDirectory = async (directoryPath) => {
  const dirRef = ref(storage, directoryPath);
  const result = await listAll(dirRef);

  // Delete all files
  await Promise.all(result.items.map((item) => deleteObject(item)));

  // Recurse into subdirectories
  await Promise.all(
    result.prefixes.map((prefix) => deleteStorageDirectory(prefix.fullPath)),
  );
};

// ============================================
// GEOGRAPHY INDEX
// ============================================

/**
 * List all shared geography entries from Firestore.
 */
export const listGeographies = async () => {
  if (DEV_MODE) {
    await new Promise((r) => setTimeout(r, 200));
    return [
      {
        id: "CT_BG",
        state: "CT",
        stateName: "Connecticut",
        geographyType: "BG",
        storagePath: "shared/geojson/BG/CT.json",
        featureCount: 2585,
        uploadedAt: new Date("2026-02-14"),
      },
      {
        id: "CT_ZCTA",
        state: "CT",
        stateName: "Connecticut",
        geographyType: "ZCTA",
        storagePath: "shared/geojson/ZCTA/CT.json",
        featureCount: 278,
        uploadedAt: new Date("2026-02-14"),
      },
    ];
  }

  try {
    const indexRef = collection(db, "shared", "geographyIndex", "files");
    const snapshot = await getDocs(indexRef);
    return snapshot.docs.map((docSnap) => ({
      id: docSnap.id,
      ...docSnap.data(),
    }));
  } catch (error) {
    console.error("Error listing geographies:", error);
    throw error;
  }
};

/**
 * Register a new shared geography file in the index.
 * @param {string} state - State abbreviation (e.g., "CT")
 * @param {string} geoType - Geography type (e.g., "ZCTA", "BG")
 * @param {object} metadata - Additional metadata (stateName, region, etc.)
 * @param {string} [metadata.region] - Optional region slug for partial/custom geographies
 */
export const registerGeography = async (state, geoType, metadata) => {
  if (DEV_MODE) {
    console.log(
      "🔧 DEV MODE: Mock register geography",
      state,
      geoType,
      metadata,
    );
    return true;
  }

  try {
    const region = metadata.region || null;
    const docId = region
      ? `${state}_${region}_${geoType}`
      : `${state}_${geoType}`;
    const storagePath = region
      ? `shared/geojson/${geoType}/${state}_${region}.json`
      : `shared/geojson/${geoType}/${state}.json`;

    const docRef = doc(db, "shared", "geographyIndex", "files", docId);
    await setDoc(docRef, {
      state,
      geographyType: geoType,
      storagePath,
      region: region || null,
      regionLabel: metadata.regionLabel || null,
      uploadedAt: serverTimestamp(),
      ...metadata,
    });
    return true;
  } catch (error) {
    console.error("Error registering geography:", error);
    throw error;
  }
};

/**
 * Delete a geography entry from the Firestore index.
 * @param {string} docId - The document ID (e.g., "CT_ZCTA" or "CA_los_angeles_BG")
 */
export const deleteGeography = async (docId) => {
  if (DEV_MODE) {
    console.log("🔧 DEV MODE: Mock delete geography", docId);
    return true;
  }

  try {
    const docRef = doc(db, "shared", "geographyIndex", "files", docId);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    console.error("Error deleting geography index entry:", error);
    throw error;
  }
};

// ============================================
// FAQ PROPAGATION
// ============================================

/**
 * Upload a new FAQ file and propagate it to all active clients.
 * @param {File} file - The faq.md file
 * @returns {Promise<{updated: string[], failed: string[]}>}
 */
export const updateFaqForAllClients = async (file) => {
  if (DEV_MODE) {
    console.log("🔧 DEV MODE: Mock FAQ propagation");
    await new Promise((r) => setTimeout(r, 1000));
    return { updated: ["laundry-lifesaver"], failed: [] };
  }

  try {
    // Get all active clients
    const clients = await listClients();
    const activeClients = clients.filter((c) => c.status === "active");

    const updated = [];
    const failed = [];

    // Upload to each client's reports directory
    for (const client of activeClients) {
      try {
        const storagePath = `clients/${client.id}/reports/faq.md`;
        await uploadFile(storagePath, file);
        updated.push(client.id);
      } catch (error) {
        console.error(`Failed to update FAQ for ${client.id}:`, error);
        failed.push(client.id);
      }
    }

    return { updated, failed };
  } catch (error) {
    console.error("Error propagating FAQ:", error);
    throw error;
  }
};

// ============================================
// VALIDATION HELPERS
// ============================================

/**
 * Validate a client slug format.
 * Must be lowercase, alphanumeric + hyphens, 3-50 chars.
 */
export const validateSlug = (slug) => {
  if (!slug) return "Slug is required";
  if (slug.length < 3) return "Slug must be at least 3 characters";
  if (slug.length > 50) return "Slug must be under 50 characters";
  if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug)) {
    return "Slug must be lowercase letters, numbers, and hyphens only (no leading/trailing hyphens)";
  }
  return null;
};

/**
 * Generate a slug from a client name.
 */
export const generateSlug = (name) => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 50);
};
